import { Flag, MoreHorizontal } from 'lucide-react'
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui'
import { cn } from '@/lib/utils'
import {
  formatTransactionAmount,
  transactionCategoryChips,
  transactionCategoryLabels,
} from './categories'
import type { Transaction, TransactionCategory } from '@/types'

interface TransactionRowProps {
  transaction: Transaction
  onFlag?: (transactionId: string) => void
  onUnflag?: (transactionId: string) => void
  onViewDetails?: (transactionId: string) => void
}

/** Rounded-square category icon chip used beside every transaction. */
export function CategoryIconChip({
  category,
  className,
}: {
  category: TransactionCategory
  className?: string
}) {
  const { icon: Icon, chipClass } =
    transactionCategoryChips[category] ?? transactionCategoryChips.OTHER
  return (
    <span
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
        chipClass,
        className
      )}
    >
      <Icon className="h-5 w-5" />
    </span>
  )
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

export function TransactionRow({
  transaction,
  onFlag,
  onUnflag,
  onViewDetails,
}: TransactionRowProps) {
  const isExpense = transaction.amount < 0
  const isSuspicious = transaction.anomaly_score > 0.7
  const hasActions = Boolean(onFlag || onUnflag || onViewDetails)

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <CategoryIconChip category={transaction.category} />

      {/* Merchant + category */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="truncate text-sm font-medium text-gray-900">
            {transaction.merchant_name || transaction.description}
          </p>
          {transaction.flagged && (
            <Flag
              className="h-3.5 w-3.5 shrink-0 text-warning-600"
              fill="currentColor"
              aria-label="Flagged"
            />
          )}
        </div>
        <p className="truncate text-xs text-gray-500">
          {transactionCategoryLabels[transaction.category]}
          {isSuspicious && !transaction.flagged && (
            <span className="text-warning-600"> · Worth a look</span>
          )}
        </p>
      </div>

      {/* Amount over date */}
      <div className="flex shrink-0 flex-col items-end">
        <span
          className={cn(
            'font-rounded text-sm font-semibold tabular-nums',
            isExpense ? 'text-gray-900' : 'text-success-600'
          )}
        >
          {isExpense ? '−' : '+'}
          {formatTransactionAmount(transaction.amount)}
        </span>
        <span className="text-xs text-gray-400">{formatDate(transaction.date)}</span>
      </div>

      {hasActions && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 shrink-0 p-0">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onViewDetails && (
              <DropdownMenuItem onClick={() => onViewDetails(transaction.id)}>
                View details
              </DropdownMenuItem>
            )}
            {onViewDetails && (onFlag || onUnflag) && <DropdownMenuSeparator />}
            {transaction.flagged
              ? onUnflag && (
                  <DropdownMenuItem onClick={() => onUnflag(transaction.id)}>
                    Looks fine — remove flag
                  </DropdownMenuItem>
                )
              : onFlag && (
                  <DropdownMenuItem onClick={() => onFlag(transaction.id)}>
                    <Flag className="mr-2 h-4 w-4" />
                    Flag for review
                  </DropdownMenuItem>
                )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
